import { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { EyeIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import Button, { BUTTON_KIND } from '@/components/Button';
import { AdditionalContactInfo } from '@/components/EditProfilePage/AdditionalContactInfo';
import { CompanyInformation } from '@/components/EditProfilePage/CompanyInfoForm';
import {
  buildPreviewSettings,
  buildProfileContent,
} from '@/components/EditProfilePage/Form';
import { PersonalInformation } from '@/components/EditProfilePage/PersonalInfoForm';
import {
  ContactExchange,
  ProfilePageAppearance,
} from '@/components/EditProfilePage/ProfileManagement';
import { ProfileQuickFileLinks } from '@/components/EditProfilePage/QuickLinksForm';
import { ProfileSocialLinks } from '@/components/EditProfilePage/SocialLinksForm';
import {
  WorkingProfileForm,
  WorkingProfileFormSchema,
} from '@/components/EditProfilePage/schema';
import { InfoPanelContainer } from '@/components/InfoPanelContainer';
import InfoPanelDivider from '@/components/InfoPanelDivider';
import InfoPanelFooter from '@/components/InfoPanelFooter';
import Layout from '@/components/Layout';
import {
  ProfilePreview,
  ProfilePreviewModal,
} from '@/components/ProfilePreview';
import COLORS from '@/constants/colors';
import { PreviewProfileProvider } from '@/hooks/usePreviewProfile';
import usePreviewProfile from '@/hooks/usePreviewProfile';

import {
  BrandingResult,
  OnboardingCompany,
  OnboardingPerson,
  OnboardingProfileData,
} from './onboardingApi';

interface Props {
  onNext: (formData: WorkingProfileForm) => void;
  onBack: () => void;
  scrapedData: OnboardingProfileData | null;
  initialFormData: WorkingProfileForm | null;
  brandingResult: BrandingResult | null;
}

const DEFAULT_SETTINGS = {
  logo: null,
  logo_file_id: null,
  bg_color: COLORS.organisation.defaultProfile.backgroundColor,
  text_color: COLORS.organisation.defaultProfile.textColor,
  button_bg_color: COLORS.organisation.defaultProfile.buttonBackgroundColor,
  button_text_color: COLORS.organisation.defaultProfile.buttonTextColor,
} as const;

function mapScrapedToDefaults(
  person: OnboardingPerson | null,
  company: OnboardingCompany | null,
): Partial<WorkingProfileForm> {
  const socialLinks: WorkingProfileForm['profile_social_links'] = [];
  let order = 0;

  if (person?.linkedInUrl) {
    socialLinks.push({
      order: order++,
      type: 'linkedin',
      value: person.linkedInUrl,
      personal: true,
    });
  }

  if (company && company.linkedInUrl) {
    socialLinks.push({
      order: order++,
      type: 'linkedin',
      value: company.linkedInUrl,
      personal: false,
    });
  }

  return {
    first_name: person?.firstName ?? null,
    last_name: person?.lastName ?? null,
    middle_name: null,
    job_title: person?.currentPosition?.title ?? person?.headline ?? null,
    department: null,
    pronouns: null,
    email: null,
    mobile_number: null,
    note: person?.summary ?? null,
    // Build a synthetic IFile so the photo previews in the form.
    // id: 0 is intentionally falsy so photo_file_id resolves to null on save
    // until the user uploads a real file.
    photo: person?.photoUrl
      ? {
          id: 0,
          name: 'linkedin-photo',
          original_url: person.photoUrl,
          large_url: person.photoUrl,
          medium_url: person.photoUrl,
          small_url: person.photoUrl,
          thumb_url: person.photoUrl,
          blur_url: person.photoUrl,
          upload_url: '',
        }
      : null,
    company_name: person?.currentPosition?.companyName ?? company?.name ?? null,
    company_website: company?.websiteUrl ?? null,
    company_phone_number: null,
    street_address: null,
    city: person?.location?.city ?? null,
    state: null,
    post_code: null,
    country: person?.location?.country ?? null,
    extra_company_phone_numbers: null,
    profile_social_links: socialLinks,
    profile_file_links: [],
    profile_quick_links: [],
    additional_information: [],
    contact_exchange_enabled: true,
    settings_level: 'profile',
    settings: DEFAULT_SETTINGS,
  };
}

// ─── Main step component ──────────────────────────────────────────────────────

function OnboardingStepTwo({
  onNext,
  onBack,
  scrapedData,
  initialFormData,
  brandingResult,
}: Props) {
  const methods = useForm<WorkingProfileForm>({
    resolver: zodResolver(WorkingProfileFormSchema),
    defaultValues:
      initialFormData ??
      mapScrapedToDefaults(
        scrapedData?.person ?? null,
        scrapedData?.company ?? null,
      ),
  });

  const { handleSubmit, setValue, setError } = methods;

  // Brand assets were fetched during the Lottie transition — apply them now.
  useEffect(() => {
    if (!brandingResult || initialFormData) return;
    const { coverImage, accentColorHex, buttonTextColor } = brandingResult;
    if (coverImage) {
      setValue('settings.logo', coverImage);
      setValue('settings.logo_file_id', null);
    }
    if (accentColorHex) {
      setValue('settings.button_bg_color', accentColorHex);
      setValue('settings.button_text_color', buttonTextColor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = (data: WorkingProfileForm) => {
    if (!data.email?.trim()) {
      setError('email', { type: 'manual', message: 'Enter email to continue' });
      toast.error('Enter email to continue');
      return;
    }

    onNext(data);
  };

  return (
    <PreviewProfileProvider previewProfile={undefined}>
      <FormProvider {...methods}>
        <Layout pageName="Set up your profile" className="bg-gray-50">
          <div className="flex flex-row pb-20 space-x-8 animate-fadeIn">
            <div className="pt-6 max-w-full">
              <PersonalInformation disableUpload />
              <InfoPanelDivider />
              <ProfileSocialLinks />
              <InfoPanelDivider />
              <CompanyInformation />
              <InfoPanelDivider />
              <ProfileQuickFileLinks />
              <InfoPanelDivider />
              <AdditionalContactInfo />
              <InfoPanelDivider />
              <InfoPanelContainer>
                <div className="text-base font-medium text-gray-900 pb-6 border-b border-gray-200">
                  Profile settings
                </div>
                <div className="space-y-8 mt-6">
                  <ContactExchange />
                  <ProfilePageAppearance disableUpload />
                </div>
              </InfoPanelContainer>
            </div>
            <OnboardingProfilePreview />
          </div>
        </Layout>

        <InfoPanelFooter>
          <div className="flex justify-between xl:justify-end space-x-4">
            <div className="xl:hidden">
              <OnboardingProfilePreviewModal />
            </div>
            <div className="flex justify-end space-x-4">
              <Button
                buttonText="Back"
                kind={BUTTON_KIND.WHITE}
                onClick={onBack}
              />
              <Button
                buttonText="Continue"
                kind={BUTTON_KIND.PRIMARY}
                onClick={handleSubmit(handleContinue)}
              />
            </div>
          </div>
        </InfoPanelFooter>

      </FormProvider>
    </PreviewProfileProvider>
  );
}

function OnboardingProfilePreview() {
  const { watch } = useFormContext<WorkingProfileForm>();
  const { previewProfile } = usePreviewProfile();

  const settingsLevel = watch('settings_level');
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    watch,
  );
  const profileContent = buildProfileContent(watch);

  return (
    <div
      className="hidden xl:block w-full max-w-[352px] md:max-w-[388px] h-fit sticky top-0 pt-8"
      style={{ zoom: 0.8 }}
    >
      <div className="p-4 rounded-md bg-white shadow-[0_0_0_1px_theme(colors.gray.300)]">
        <ProfilePreview
          content={profileContent}
          settings={settings}
          showDefaultProfilePicture={false}
        />
      </div>
    </div>
  );
}

function OnboardingProfilePreviewModal() {
  const { getValues } = useFormContext<WorkingProfileForm>();
  const { previewProfile } = usePreviewProfile();
  const [isOpen, setIsOpen] = useState(false);

  const settingsLevel = getValues('settings_level');
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    getValues,
  );
  const profileContent = buildProfileContent(getValues);

  return (
    <>
      <Button
        buttonText="Preview"
        kind={BUTTON_KIND.WHITE}
        icon={<EyeIcon />}
        onClick={() => setIsOpen(true)}
      />
      <ProfilePreviewModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        settings={settings}
        content={profileContent}
      />
    </>
  );
}

export default OnboardingStepTwo;
